const { Weight } = require('./units');

const DEFAULT_MAX_IMAGES = 6;
const DEFAULT_MAX_TITLE_LENGTH = 120;
const DEFAULT_MAX_DESCRIPTION_LENGTH = 500;
const DEFAULT_MAX_ANALYSIS_ITEMS = 20;
const MAX_ANALYSIS_QUANTITY = 100000;

const normalizeFoodType = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replaceAll('_', '-')
    .replace(/\s+/g, '-');

const parseImages = (images) => {
  if (Array.isArray(images)) return images;
  if (typeof images !== 'string' || images.length === 0) return [];

  try {
    const parsed = JSON.parse(images);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const normalizeImage = (image) => {
  if (typeof image === 'string') return image;
  return image?.image ?? image?.url ?? image?.file?.path ?? null;
};

const getInventoryImages = (item) => {
  const primaryImage = normalizeImage(item?.primary_image);
  const images = parseImages(item?.images).map(normalizeImage).filter(Boolean);
  return [...new Set([primaryImage, ...images].filter(Boolean))];
};

const buildDescription = (items, maxLength) => {
  const describedItems = items.filter(
    (item) => typeof item?.description === 'string' && item.description.trim()
  );

  if (describedItems.length === 0) {
    return { description: '', descriptionTruncated: false };
  }

  const description =
    describedItems.length === 1
      ? describedItems[0].description.trim()
      : describedItems
          .map((item) => {
            const name = String(item.name ?? '').trim();
            const itemDescription = item.description.trim();
            return name ? `${name}: ${itemDescription}` : itemDescription;
          })
          .join('\n\n');

  if (description.length <= maxLength) {
    return { description, descriptionTruncated: false };
  }

  return {
    description: description.slice(0, maxLength).trimEnd(),
    descriptionTruncated: true,
  };
};

const buildTitle = (items, maxLength) =>
  items
    .map((item) => String(item?.name ?? '').trim())
    .filter(Boolean)
    .join(', ')
    .slice(0, maxLength)
    .trimEnd()
    .replace(/,$/, '');

const cleanAnalysisText = (value, maxLength) => {
  if (typeof value !== 'string') return null;
  const cleaned = value
    .replace(/["\u201C\u201D]/g, '')
    .trim()
    .slice(0, maxLength);
  return cleaned || null;
};

const normalizeComparisonText = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const combineInventoryAndAnalysisTitle = (items, suggestedTitle, maxLength) => {
  const analysisTitle = cleanAnalysisText(suggestedTitle, maxLength);
  const normalizedAnalysisTitle = normalizeComparisonText(analysisTitle);
  const names = [];
  const seenNames = new Set();

  for (const item of items) {
    const name = cleanAnalysisText(item?.name, maxLength);
    const normalizedName = normalizeComparisonText(name);
    if (!name || seenNames.has(normalizedName)) continue;

    seenNames.add(normalizedName);
    if (!normalizedAnalysisTitle.includes(normalizedName)) {
      names.push(name);
    }
  }

  return [...names, analysisTitle]
    .filter(Boolean)
    .join(', ')
    .slice(0, maxLength)
    .trimEnd()
    .replace(/,$/, '');
};

const combineInventoryAndAnalysisDescription = (
  inventoryDescription,
  suggestedDescription,
  maxLength
) => {
  const analysisDescription = cleanAnalysisText(
    suggestedDescription,
    maxLength
  );
  const normalizedInventoryDescription =
    normalizeComparisonText(inventoryDescription);
  const normalizedAnalysisDescription =
    normalizeComparisonText(analysisDescription);
  let parts;

  if (
    normalizedInventoryDescription &&
    normalizedAnalysisDescription.includes(normalizedInventoryDescription)
  ) {
    parts = [analysisDescription];
  } else if (
    normalizedAnalysisDescription &&
    normalizedInventoryDescription.includes(normalizedAnalysisDescription)
  ) {
    parts = [inventoryDescription];
  } else {
    parts = [inventoryDescription, analysisDescription];
  }

  const fullDescription = parts.filter(Boolean).join('\n\n');
  return {
    description: fullDescription.slice(0, maxLength).trimEnd(),
    descriptionTruncated: fullDescription.length > maxLength,
  };
};

const buildInventoryAnalysisItems = (
  selectedItems,
  { maxItems = DEFAULT_MAX_ANALYSIS_ITEMS } = {}
) => {
  const items = Array.isArray(selectedItems) ? selectedItems : [];
  const analysisItems = [];
  const seenInventoryIds = new Set();

  for (const item of items) {
    const inventoryItemId = item?.id ?? item?.inventoryItemId;
    if (
      typeof inventoryItemId !== 'string' ||
      !inventoryItemId.trim() ||
      seenInventoryIds.has(inventoryItemId) ||
      analysisItems.length >= maxItems
    ) {
      continue;
    }

    const weightValue = Number(item.weight_per_item ?? item.weightPerItem);
    const quantityValue = Number(item.quantity ?? 1);
    seenInventoryIds.add(inventoryItemId);
    analysisItems.push({
      inventoryItemId: inventoryItemId.trim(),
      name: cleanAnalysisText(item.name, 120),
      description: cleanAnalysisText(item.description, 500),
      foodType: normalizeFoodType(item.food_type ?? item.foodType) || null,
      weightPerItem:
        Number.isFinite(weightValue) && weightValue >= 0 ? weightValue : null,
      weightUnit:
        cleanAnalysisText(
          item.weight_per_item_unit ?? item.weightPerItemUnit,
          16
        ) ?? (Number.isFinite(weightValue) && weightValue >= 0 ? 'lbs' : null),
      selectedQuantity:
        Number.isInteger(quantityValue) &&
        quantityValue > 0 &&
        quantityValue <= MAX_ANALYSIS_QUANTITY
          ? quantityValue
          : 1,
    });
  }

  return analysisItems;
};

const applyInventoryAnalysisQuantities = (selectedItems, analyzedItems) => {
  const items = Array.isArray(selectedItems) ? selectedItems : [];
  const quantities = new Map();

  for (const item of Array.isArray(analyzedItems) ? analyzedItems : []) {
    const inventoryItemId = item?.inventoryItemId;
    const quantity = Number(item?.quantity);
    if (
      typeof inventoryItemId === 'string' &&
      inventoryItemId.trim() &&
      Number.isInteger(quantity) &&
      quantity > 0 &&
      quantity <= MAX_ANALYSIS_QUANTITY
    ) {
      quantities.set(inventoryItemId.trim(), quantity);
    }
  }

  return items.map((item) => {
    const inventoryItemId = item?.id ?? item?.inventoryItemId;
    const quantity = quantities.get(inventoryItemId);
    return quantity ? { ...item, quantity } : item;
  });
};

const selectInventoryPhotosForDonation = (
  inventoryImages,
  userImages,
  { maxImages = DEFAULT_MAX_IMAGES } = {}
) => {
  if (Array.isArray(userImages) && userImages.length > 0) return [];
  return (Array.isArray(inventoryImages) ? inventoryImages : []).slice(
    0,
    Math.max(0, maxImages)
  );
};

const inventoryImageKey = (image) => {
  const url = image?.url;
  if (!url) return null;
  return `${image?.inventoryItemId ?? ''}\u0000${url}`;
};

const retainInventoryPhotosForDonation = (
  availableImages,
  previouslyAttachedImages,
  currentlyAttachedImages
) => {
  const previousKeys = new Set(
    (Array.isArray(previouslyAttachedImages) ? previouslyAttachedImages : [])
      .map(inventoryImageKey)
      .filter(Boolean)
  );
  const currentKeys = new Set(
    (Array.isArray(currentlyAttachedImages) ? currentlyAttachedImages : [])
      .map(inventoryImageKey)
      .filter(Boolean)
  );

  return (Array.isArray(availableImages) ? availableImages : []).filter(
    (image) => {
      const key = inventoryImageKey(image);
      return key && (!previousKeys.has(key) || currentKeys.has(key));
    }
  );
};

const buildInventoryDonationPrefill = (
  selectedItems,
  {
    weightUnit = 'lbs',
    maxImages = DEFAULT_MAX_IMAGES,
    maxTitleLength = DEFAULT_MAX_TITLE_LENGTH,
    maxDescriptionLength = DEFAULT_MAX_DESCRIPTION_LENGTH,
  } = {}
) => {
  const items = Array.isArray(selectedItems) ? selectedItems : [];
  const categories = [];
  const weights = {};
  const inventoryItems = [];
  const images = [];
  const uniqueItems = [];
  const itemImages = [];
  const seenInventoryIds = new Set();
  const seenImages = new Set();

  for (const item of items) {
    const inventoryItemId = item?.id ?? item?.inventoryItemId;
    if (!inventoryItemId || seenInventoryIds.has(inventoryItemId)) continue;

    const quantityValue = Number(item.quantity ?? 1);
    const quantity =
      Number.isInteger(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const foodType = normalizeFoodType(item.food_type ?? item.foodType);
    const weightValue = Number(item.weight_per_item ?? item.weightPerItem);
    const sourceWeightUnit =
      item.weight_per_item_unit ?? item.weightPerItemUnit ?? 'lbs';

    seenInventoryIds.add(inventoryItemId);
    uniqueItems.push(item);
    inventoryItems.push({ inventoryItemId, quantity });
    itemImages.push({
      inventoryItemId,
      images: getInventoryImages(item),
    });

    if (foodType && Number.isFinite(weightValue) && weightValue >= 0) {
      const convertedWeight = new Weight(
        weightValue * quantity,
        sourceWeightUnit
      ).as(weightUnit);

      if (!weights[foodType]) {
        categories.push(foodType);
        weights[foodType] = { value: 0, unit: weightUnit };
      }
      weights[foodType].value += convertedWeight;
    }
  }

  const addImage = (image, inventoryItemId) => {
    if (!image || images.length >= maxImages || seenImages.has(image)) {
      return false;
    }
    seenImages.add(image);
    images.push({ url: image, inventoryItemId });
    return true;
  };

  // Give every selected item a photo before filling remaining slots.
  for (const item of itemImages) {
    const firstAvailableImage = item.images.find(
      (image) => !seenImages.has(image)
    );
    addImage(firstAvailableImage, item.inventoryItemId);
  }
  for (const item of itemImages) {
    for (const image of item.images) {
      addImage(image, item.inventoryItemId);
    }
  }

  for (const foodType of Object.keys(weights)) {
    weights[foodType].value = Number(weights[foodType].value.toFixed(4));
  }

  const { description, descriptionTruncated } = buildDescription(
    uniqueItems,
    maxDescriptionLength
  );
  const title = buildTitle(uniqueItems, maxTitleLength);

  return {
    categories,
    weights,
    title,
    description,
    descriptionTruncated,
    images,
    inventoryItems,
  };
};

const mergeInventoryDonationAnalysis = (
  selectedItems,
  analysis,
  {
    weightUnit = 'lbs',
    validFoodTypes = null,
    minAnalysisWeightLbs = 0.1,
    analysisRoundThresholdLbs = 10,
    analysisDecimalPlaces = 1,
    maxTitleLength = DEFAULT_MAX_TITLE_LENGTH,
    maxDescriptionLength = DEFAULT_MAX_DESCRIPTION_LENGTH,
  } = {}
) => {
  const updatedSelectedItems = applyInventoryAnalysisQuantities(
    selectedItems,
    analysis?.inventoryItems
  );
  const inventoryPrefill = buildInventoryDonationPrefill(updatedSelectedItems, {
    weightUnit,
    maxTitleLength,
    maxDescriptionLength,
  });
  const allowedFoodTypes = Array.isArray(validFoodTypes)
    ? new Set(validFoodTypes.map(normalizeFoodType))
    : null;
  const categories = [];
  const weights = {};
  const analysisCategories = [];
  const analysisWeights = {};

  for (const category of inventoryPrefill.categories) {
    if (allowedFoodTypes && !allowedFoodTypes.has(category)) continue;
    categories.push(category);
    weights[category] = { ...inventoryPrefill.weights[category] };
  }

  for (const categoryData of Array.isArray(analysis?.categories)
    ? analysis.categories
    : []) {
    const category = normalizeFoodType(categoryData?.category);
    const weightInLbs = Number(categoryData?.weight);
    if (
      !category ||
      (allowedFoodTypes && !allowedFoodTypes.has(category)) ||
      !Number.isFinite(weightInLbs) ||
      weightInLbs < minAnalysisWeightLbs
    ) {
      continue;
    }

    const roundedWeightInLbs =
      weightInLbs >= analysisRoundThresholdLbs
        ? Math.round(weightInLbs)
        : Number(weightInLbs.toFixed(analysisDecimalPlaces));

    if (!analysisWeights[category]) {
      analysisCategories.push(category);
      analysisWeights[category] = { value: 0, unit: 'lbs' };
    }
    analysisWeights[category].value += roundedWeightInLbs;

    if (!weights[category]) {
      categories.push(category);
      weights[category] = { value: 0, unit: 'lbs' };
    }

    const targetUnit = weights[category].unit;
    weights[category].value += new Weight(roundedWeightInLbs, 'lbs').as(
      targetUnit
    );
  }

  for (const category of Object.keys(weights)) {
    weights[category].value = Number(weights[category].value.toFixed(4));
  }
  for (const category of Object.keys(analysisWeights)) {
    analysisWeights[category].value = Number(
      analysisWeights[category].value.toFixed(4)
    );
  }

  const title = combineInventoryAndAnalysisTitle(
    updatedSelectedItems,
    analysis?.suggestedTitle,
    maxTitleLength
  );
  const combinedDescription = combineInventoryAndAnalysisDescription(
    inventoryPrefill.description,
    analysis?.suggestedDescription,
    maxDescriptionLength
  );

  return {
    selectedInventoryItems: updatedSelectedItems,
    inventoryPrefill,
    categories,
    weights,
    analysisCategories,
    analysisWeights,
    title,
    description: combinedDescription.description,
    descriptionTruncated:
      inventoryPrefill.descriptionTruncated ||
      combinedDescription.descriptionTruncated,
  };
};

const toDonationInventoryPayload = (items) =>
  (Array.isArray(items) ? items : [])
    .map((item) => ({
      inventoryItemId: item?.inventoryItemId || item?.id || null,
      quantity: Number(item?.quantity ?? 1) || 1,
    }))
    .filter(
      (item) => typeof item.inventoryItemId === 'string' && item.inventoryItemId
    );

// Stock copies of inventory photos are named inventory-<uuid>.<ext> (#609).
const STOCK_PHOTO_PREFIX = 'inventory-';

const isStockPhotoKey = (key) =>
  typeof key === 'string' &&
  key.split('/').pop().startsWith(STOCK_PHOTO_PREFIX);

// True when a donation has photos and none of them were uploaded for it.
const hasOnlyStockPhotos = (keys) =>
  Array.isArray(keys) && keys.length > 0 && keys.every(isStockPhotoKey);

module.exports = {
  STOCK_PHOTO_PREFIX,
  applyInventoryAnalysisQuantities,
  buildInventoryAnalysisItems,
  buildInventoryDonationPrefill,
  hasOnlyStockPhotos,
  isStockPhotoKey,
  mergeInventoryDonationAnalysis,
  normalizeInventoryDonationFoodType: normalizeFoodType,
  retainInventoryPhotosForDonation,
  selectInventoryPhotosForDonation,
  toDonationInventoryPayload,
};
